import React, { forwardRef, useEffect, useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { border, color, shadow, typography } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { Portal } from '../portal/Portal';
import { Icon } from '../icon/Icon';
import { Button } from '../button/Button';
import { Box, Flex } from '../grid/Grid';
import { Heading } from '../heading/Heading';

const Mask = styled(Box)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-y: auto;
  background-color: ${themeGet('colors.blacks.5')};
  z-index: ${themeGet('zIndex.modalMask')};
`;

const Header = styled(Flex)`
  align-items: center;
  ${typography}

  ${props =>
    props.divider &&
    css`
      border-bottom: ${themeGet('modal.header.borderBottom')};
      border-color: ${themeGet('modal.header.borderColor')};
    `}
`;

const CloseButton = styled(Button).attrs({
  variant: 'link',
})`
  position: absolute;
  top: ${themeGet('modal.closeButton.top')};
  right: ${themeGet('modal.closeButton.right')};
  width: ${themeGet('modal.closeButton.size')};
  height: ${themeGet('modal.closeButton.size')};
  background-color: ${themeGet('modal.closeButton.backgroundColor')};
  border-radius: ${themeGet('modal.closeButton.borderRadius')};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  cursor: pointer;
  z-index: ${themeGet('zIndex.fixed')};
`;

const CloseButtonIcon = styled(Icon).attrs({
  name: 'CLOSE',
  color: 'blacks.3',
})`
  width: ${themeGet('modal.closeButton.size')};
  height: ${themeGet('modal.closeButton.size')};
  color: ${themeGet('modal.closeButton.color')};
`;

const Content = styled(Box)`
  position: relative;
  z-index: ${themeGet('zIndex.modal')};
  box-shadow: ${themeGet('modal.content.boxShadow')};
  background-color: ${themeGet('modal.content.backgroundColor')};
  border: ${themeGet('modal.content.border')};
  border-radius: ${themeGet('modal.content.borderRadius')};
  border-color: ${themeGet('modal.content.borderColor')};
  ${border}
`;

const Body = styled(Box)`
  ${color}
`;

const Footer = styled(Flex)`
  width: 100%;
  align-items: center;
  z-index: ${themeGet('zIndex.fixed')};
  ${shadow}

  ${props =>
    props.divider &&
    css`
      border-top: ${themeGet('modal.footer.borderTop')};
      border-color: ${themeGet('modal.footer.borderColor')};
    `}
`;

export const Modal = forwardRef(function Modal(
  { children, show, closeButton, onClose, maskClosable = true, ...props },
  ref
) {
  useEffect(() => {
    show ? disableBodyScroll() : clearAllBodyScrollLocks();
  }, [show]);
  const {
    modal: { content: defaultContentStyle },
  } = useContext(ThemeContext);
  return (
    show && (
      <Portal>
        <Mask onClick={maskClosable && onClose}>
          <Content ref={ref} {...defaultContentStyle} {...props} onClick={event => event.stopPropagation()}>
            {React.isValidElement(closeButton)
              ? closeButton
              : closeButton && (
                  <CloseButton variant="link" onClick={onClose}>
                    <CloseButtonIcon />
                  </CloseButton>
                )}
            {children}
          </Content>
        </Mask>
      </Portal>
    )
  );
});

export const ModalHeader = forwardRef(function ModalHeader(
  { children, divider = true, fontSize, ...props },
  ref
) {
  const {
    modal: { header: defaultHeaderStyle },
  } = useContext(ThemeContext);
  const {
    heading: { fontSize: defaultFontSize },
  } = defaultHeaderStyle;
  return (
    <Header divider={divider} {...defaultHeaderStyle} {...props} ref={ref}>
      <Heading as="div" width={1} fontSize={fontSize || defaultFontSize}>
        {children}
      </Heading>
    </Header>
  );
});

export const ModalBody = forwardRef(function ModalBody({ ...props }, ref) {
  const {
    modal: { body: defaultBodyStyle },
  } = useContext(ThemeContext);
  return <Body ref={ref} {...defaultBodyStyle} {...props} />;
});

export const ModalFooter = forwardRef(function ModalFooter({ divider = false, ...props }, ref) {
  const {
    modal: { footer: defaultFooterStyle },
  } = useContext(ThemeContext);
  return <Footer divider={divider} ref={ref} as="footer" {...defaultFooterStyle} {...props} />;
});
